import React, { useState, useEffect } from 'react';
/* global axios */
import api from '../services/apiService';
import { Helmet } from 'react-helmet';

const TelematicsProviderForm = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Felder für NEUEN Provider
  const [newName, setNewName] = useState('');
  const [newBaseUrl, setNewBaseUrl] = useState('');
  const [newAuthType, setNewAuthType] = useState('');
  const [newApiKey, setNewApiKey] = useState('');
  const [newClientId, setNewClientId] = useState('');
  const [newClientSecret, setNewClientSecret] = useState('');
  const [newConfigKey, setNewConfigKey] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newIsActive, setNewIsActive] = useState(true);

  // Bearbeitungs-States
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editedBaseUrl, setEditedBaseUrl] = useState('');
  const [editedAuthType, setEditedAuthType] = useState('');
  const [editedApiKey, setEditedApiKey] = useState('');
  const [editedClientId, setEditedClientId] = useState('');
  const [editedClientSecret, setEditedClientSecret] = useState('');
  const [editedConfigKey, setEditedConfigKey] = useState('');
  const [editedNotes, setEditedNotes] = useState('');
  const [editedIsActive, setEditedIsActive] = useState(true);

  // Provider laden
  const fetchProviders = async () => {
    try {
      const response = await api.get('/telematics/providers');
      // Backend gibt entweder { ok, data: [...] } oder direkt Array zurück
      const list = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];

      const sorted = list.sort((a, b) => a.name.localeCompare(b.name));
      setProviders(sorted);
    } catch (error) {
      console.error('Fehler beim Laden der Telematik-Provider:', error);
      alert('Fehler beim Laden der Telematik-Provider');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  // Hinzufügen
  const handleAddProvider = async () => {
    if (!newName) {
      alert('Bitte einen Namen für den Telematik-Provider eingeben!');
      return;
    }

    try {
      await api.post('/telematics/providers', {
        name: newName,
        baseUrl: newBaseUrl || null,
        authType: newAuthType || null,
        apiKey: newApiKey || null,
        clientId: newClientId || null,
        clientSecret: newClientSecret || null,
        configKey: newConfigKey || null,
        notes: newNotes || null,
        isActive: newIsActive,
      });

      // Felder leeren
      setNewName('');
      setNewBaseUrl('');
      setNewAuthType('');
      setNewApiKey('');
      setNewClientId('');
      setNewClientSecret('');
      setNewConfigKey('');
      setNewNotes('');
      setNewIsActive(true);

      fetchProviders();
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Providers:', error);
      alert('Fehler beim Hinzufügen des Providers');
    }
  };

  // Löschen
  const handleDeleteProvider = async (id) => {
    if (!window.confirm('Diesen Telematik-Provider wirklich löschen?')) return;

    try {
      await api.delete(`/telematics/providers/${id}`);
      setProviders(providers.filter((p) => p.id !== id));
      alert('Telematik-Provider gelöscht!');
    } catch (error) {
      console.error('Fehler beim Löschen des Providers:', error);
      alert('Fehler beim Löschen des Providers');
    }
  };

  // Bearbeiten starten
  const handleEditProvider = (provider) => {
    setEditingId(provider.id);
    setEditedName(provider.name || '');
    setEditedBaseUrl(provider.baseUrl || '');
    setEditedAuthType(provider.authType || '');
    setEditedApiKey(provider.apiKey || '');
    setEditedClientId(provider.clientId || '');
    setEditedClientSecret(provider.clientSecret || '');
    setEditedConfigKey(provider.configKey || '');
    setEditedNotes(provider.notes || '');
    setEditedIsActive(provider.isActive ?? true);
  };

  // Bearbeiten abbrechen
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedName('');
    setEditedBaseUrl('');
    setEditedAuthType('');
    setEditedApiKey('');
    setEditedClientId('');
    setEditedClientSecret('');
    setEditedConfigKey('');
    setEditedNotes('');
    setEditedIsActive(true);
  };

  // Aktualisieren
  const handleUpdateProvider = async (id) => {
    try {
      await api.put(`/telematics/providers/${id}`, {
        name: editedName,
        baseUrl: editedBaseUrl || null,
        authType: editedAuthType || null,
        apiKey: editedApiKey || null,
        clientId: editedClientId || null,
        clientSecret: editedClientSecret || null,
        configKey: editedConfigKey || null,
        notes: editedNotes || null,
        isActive: editedIsActive,
      });

      setEditingId(null);
      fetchProviders();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Providers:', error);
      alert('Fehler beim Aktualisieren des Providers: ' + error.message);
    }
  };

  if (loading) {
    return <div>Daten werden geladen...</div>;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
      <div style={{ width: '1100px' }}>
        <Helmet>
          <title>Telematik-Provider verwalten</title>
          <meta name="description" content="Telematik-Provider verwalten" />
        </Helmet>

        <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>
          Telematik-Provider verwalten
        </h2>

        {/* Hinzufügen-Formular (mehrzeilig, damit alle Felder Platz haben) */}
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 1fr auto',
              gap: '10px',
              marginBottom: '10px',
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name *"
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <input
              type="text"
              value={newBaseUrl}
              onChange={(e) => setNewBaseUrl(e.target.value)}
              placeholder="Base URL (optional)"
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <input
              type="text"
              value={newAuthType}
              onChange={(e) => setNewAuthType(e.target.value)}
              placeholder="Auth-Typ (z.B. apiKey)"
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                justifyContent: 'flex-start',
              }}
            >
              <input
                type="checkbox"
                checked={newIsActive}
                onChange={(e) => setNewIsActive(e.target.checked)}
              />
              aktiv
            </label>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '10px',
              marginBottom: '10px',
            }}
          >
            <input
              type="text"
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              placeholder="API Key (optional)"
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <input
              type="text"
              value={newClientId}
              onChange={(e) => setNewClientId(e.target.value)}
              placeholder="Client ID (optional)"
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <input
              type="text"
              value={newClientSecret}
              onChange={(e) => setNewClientSecret(e.target.value)}
              placeholder="Client Secret (optional)"
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr auto',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              value={newConfigKey}
              onChange={(e) => setNewConfigKey(e.target.value)}
              placeholder="Config Key (optional)"
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <input
              type="text"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              placeholder="Notizen (optional)"
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
            <button
              onClick={handleAddProvider}
              style={{
                padding: '6px 12px',
                background: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Hinzufügen
            </button>
          </div>
        </div>

        {/* Listenansicht */}
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {providers.map((provider) => (
            <li
              key={provider.id}
              style={{
                borderBottom: '1px solid #ddd',
                padding: '10px 0',
              }}
            >
              {editingId === provider.id ? (
                <>
                  {/* erste Zeile: Basisdaten */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 2fr 1fr auto',
                      gap: '10px',
                      marginBottom: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <input
                      type="text"
                      value={editedBaseUrl}
                      onChange={(e) => setEditedBaseUrl(e.target.value)}
                      style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <input
                      type="text"
                      value={editedAuthType}
                      onChange={(e) => setEditedAuthType(e.target.value)}
                      placeholder="Auth-Typ"
                      style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={editedIsActive}
                        onChange={(e) => setEditedIsActive(e.target.checked)}
                      />
                      aktiv
                    </label>
                  </div>

                  {/* zweite Zeile: Auth / Config */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '10px',
                      marginBottom: '8px',
                    }}
                  >
                    <input
                      type="text"
                      value={editedApiKey}
                      onChange={(e) => setEditedApiKey(e.target.value)}
                      placeholder="API Key"
                      style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <input
                      type="text"
                      value={editedClientId}
                      onChange={(e) => setEditedClientId(e.target.value)}
                      placeholder="Client ID"
                      style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <input
                      type="text"
                      value={editedClientSecret}
                      onChange={(e) => setEditedClientSecret(e.target.value)}
                      placeholder="Client Secret"
                      style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>

                  {/* dritte Zeile: Config + Notes + Buttons */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 2fr auto',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="text"
                      value={editedConfigKey}
                      onChange={(e) => setEditedConfigKey(e.target.value)}
                      placeholder="Config Key"
                      style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <input
                      type="text"
                      value={editedNotes}
                      onChange={(e) => setEditedNotes(e.target.value)}
                      placeholder="Notizen"
                      style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <div>
                      <button
                        onClick={() => handleUpdateProvider(provider.id)}
                        style={{ marginRight: '5px' }}
                      >
                        ✅
                      </button>
                      <button onClick={handleCancelEdit}>✖️</button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Anzeige-Modus */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 2fr 1fr auto',
                      gap: '10px',
                      alignItems: 'center',
                      marginBottom: '4px',
                    }}
                  >
                    <span>{provider.name}</span>
                    <span>{provider.baseUrl}</span>
                    <span>{provider.authType || <span style={{ color: '#666' }}>ohne</span>}</span>
                    <div>
                      <span
                        style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: provider.isActive ? '#d4edda' : '#f8d7da',
                          color: provider.isActive ? '#155724' : '#721c24',
                          fontSize: '12px',
                        }}
                      >
                        {provider.isActive ? 'aktiv' : 'inaktiv'}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr auto',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontSize: '12px', color: '#555' }}>
                      API Key: {provider.apiKey ? '••••••' : <em>keiner</em>}
                    </span>
                    <span style={{ fontSize: '12px', color: '#555' }}>
                      Client: {provider.clientId || <em>keiner</em>}
                    </span>
                    <span style={{ fontSize: '12px', color: '#555' }}>
                      Config: {provider.configKey || <em>keiner</em>}
                    </span>
                    <div>
                      <button
                        onClick={() => handleEditProvider(provider)}
                        style={{
                          fontSize: '15px',
                          padding: '5px 10px',
                          background: '#007bff',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          marginRight: '5px',
                        }}
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDeleteProvider(provider.id)}
                        style={{
                          fontSize: '15px',
                          padding: '5px 10px',
                          background: '#dc3545',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                        }}
                      >
                        Löschen
                      </button>
                    </div>
                  </div>

                  {provider.notes && (
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#444' }}>
                      Notizen: {provider.notes}
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default TelematicsProviderForm;
